import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, Download, Trash2, Loader2, FileText, Image as ImageIcon, FileWarning } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ExpenseReceiptsPanelProps {
  expenseId: string;
}

interface ExpenseDocument {
  id: string;
  expenseId: string | null;
  fileName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  filePath: string;
  category: string | null;
  description: string | null;
  uploadedBy: string;
  uploaderName: string;
  createdAt: string;
  updatedAt: string;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function iconFor(mime: string) {
  if (mime.startsWith('image/')) return ImageIcon;
  if (mime === 'application/pdf') return FileText;
  return FileWarning;
}

export function ExpenseReceiptsPanel({ expenseId }: ExpenseReceiptsPanelProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['expense-documents', expenseId],
    queryFn: async () => {
      const res = await api.getExpenseDocuments(expenseId);
      return (res.data ?? []) as ExpenseDocument[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.deleteDocument(id);
      if (!res.success) throw new Error(res.error?.message || 'Delete failed');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense-documents', expenseId] });
      toast({ title: 'Receipt deleted' });
    },
    onError: (err: Error) => toast({ title: 'Delete failed', description: err.message, variant: 'destructive' }),
  });

  async function handleDownload(doc: ExpenseDocument) {
    const token = api.getAccessToken();
    const res = await fetch(api.getDocumentDownloadUrl(doc.id), {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      toast({ title: 'Download failed', variant: 'destructive' });
      return;
    }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = doc.originalName;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function handleUpload(file: File) {
    setUploading(true);
    try {
      const res = await api.uploadDocument(file, { expenseId, category: 'receipt' });
      if (!res.success) throw new Error(res.error?.message || 'Upload failed');
      qc.invalidateQueries({ queryKey: ['expense-documents', expenseId] });
      toast({ title: 'Receipt uploaded', description: file.name });
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const docs = data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Receipts / Supporting Documents</Label>
        <div>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept="application/pdf,image/jpeg,image/png,image/gif,image/webp,image/tiff,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {uploading
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Upload className="h-4 w-4 mr-2" />}
            Upload
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : docs.length === 0 ? (
        <p className="text-xs text-muted-foreground border rounded-md p-3 text-center">
          No receipts attached yet. Upload PDFs, images, or statements for audit support.
        </p>
      ) : (
        <ul className="border rounded-md divide-y">
          {docs.map((doc) => {
            const Icon = iconFor(doc.mimeType);
            return (
              <li key={doc.id} className="flex items-center gap-3 p-2">
                <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{doc.originalName}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatSize(doc.fileSize)} · {new Date(doc.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownload(doc)}
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  onClick={() => {
                    if (confirm(`Delete ${doc.originalName}?`)) deleteMutation.mutate(doc.id);
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
